import { Appointment, AppointmentStatuses } from '@chiroup/core/types/Appointment.type';
import { useState } from 'react';
import { QueryFunctionContext, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import locationService from '../services/location.service';

const getQuery = () => {
  return async (context: QueryFunctionContext) => {
    const path = context.queryKey[1] as string | null | undefined;
    const clinicId = (context.queryKey[2] as number | null | undefined) || null;
    const patientId = (context.queryKey[3] as string | null | undefined) || null;

    // Path-based (legacy scheduling-site) takes precedence; the authed
    // portal falls back to the clinicId variant for clinics with no
    // vanity URL. Always returns an array so callers can `.slice()`.
    if (path) {
      return locationService.getAppointments(path, patientId);
    }

    if (clinicId) {
      return locationService.getAppointmentsByClinicId(clinicId, patientId);
    }

    return [];
  };
};

/**
 * Fetches the authenticated user's appointments at a clinic.
 *
 * The authed patient portal no longer uses URL paths to identify a
 * clinic, so callers pass in `overridePath` sourced from the selected
 * clinic state (see `Container.tsx`). Falls back to `useParams().path`
 * for the legacy scheduling-site entry point (pre-login, URL-based).
 *
 * `patientId` (optional) scopes the response to a single patient. The
 * portal passes the currently-selected family member's ID; when
 * omitted the backend returns appointments for every patient sharing
 * the logged-in user's email/phone (legacy callers only).
 */
const useAppointments = (
  overridePath?: string | null,
  patientId?: string | null,
  clinicId?: number | null
) => {
  const { path: urlPath } = useParams();
  const path = overridePath || urlPath;
  const queryClient = useQueryClient();
  const [isCancelling, setIsCancelling] = useState(false);

  // patientId and clinicId are part of the cache key so switching family
  // members or clinics doesn't reuse the previous list.
  const queryKey = ['appointments', path, clinicId || null, patientId || null] as const;

  const { status, data, error, isFetching, refetch } = useQuery<Appointment[]>(
    queryKey,
    getQuery(),
    {
      // Without a path or clinicId there's nothing to fetch — leaving the
      // query enabled would run the fn and (pre-fix) return a non-array
      // that crashed `Appointments.tsx`'s `.slice()`.
      enabled: !!(path || clinicId),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const cancel = async (appointmentId: string, reason: string) => {
    setIsCancelling(true);

    try {
      await locationService.cancelAppointment(appointmentId, reason);
    } catch (e) {
      setIsCancelling(false);
      throw e;
    }

    queryClient.setQueryData(queryKey, (prev?: Appointment[]) => {
      if (!prev) return [];
      const canceledAppt = prev.find(item => item.id === appointmentId);

      if (!canceledAppt) {
        return prev;
      }

      const updatedData = prev.map(item => {
        if (item.groupId === canceledAppt.groupId) {
          return {
            ...item,
            status: AppointmentStatuses.Canceled,
          };
        }

        return item;
      });

      return updatedData;
    });
    setIsCancelling(false);
  };

  const updateAppointment = (id: string, data: Partial<Appointment>) => {
    queryClient.setQueryData(queryKey, (prev?: Appointment[]) => {
      return (
        prev?.map((appt: any) => {
          if (appt.id === id) {
            return {
              ...appt,
              ...data,
            };
          }

          return appt;
        }) || []
      );
    });
  };

  return {
    status,
    data,
    error,
    isFetching,
    refetch,
    cancel,
    isCancelling,
    updateAppointment,
  };
};

export default useAppointments;
